import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warehouse as WarehouseIcon,
  ArrowBack as BackIcon,
  Inventory as InventoryIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Image as ImageIcon,
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import WarehouseBrandSubsidiaryMatrix from './WarehouseBrandSubsidiaryMatrix';
import {
  getWarehouses,
  getWarehouse,
  createWarehouse,
  updateWarehouse,
  getWarehouseInventory,
  bulkImportWarehouses,
  downloadCSVTemplate,
  formatCurrency,
  formatNumber
} from '../../services/inventoryService';
import api from '../../config/api';
import { formatCountry } from '../../utils/countryFormat';
import { Timeline } from '../ds';
import { useEntityTimeline } from '../../services/auditTimelineService';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Currency options for the warehouse Currency dropdown (common trading currencies).
const CURRENCIES = ['USD','GBP','EUR','AUD','CAD','NZD','SGD','HKD','JPY','CNY','INR','AED','SAR','CHF','SEK','NOK','DKK','ZAR','MXN','BRL','PLN','CZK','THB','MYR','IDR','PHP','KRW','TWD','VND','TRY'];

// Map warehouse codes to Amazon domains
const getAmazonDomain = (warehouseCode) => {
  if (!warehouseCode) return 'amazon.com';
  const code = warehouseCode.toUpperCase();
  if (code.startsWith('UK-')) return 'amazon.co.uk';
  if (code.startsWith('AU-')) return 'amazon.com.au';
  if (code.startsWith('CA-')) return 'amazon.ca';
  if (code.startsWith('EU-') || code.startsWith('DE-')) return 'amazon.de';
  if (code.startsWith('FR-')) return 'amazon.fr';
  if (code.startsWith('IT-')) return 'amazon.it';
  if (code.startsWith('ES-')) return 'amazon.es';
  if (code.startsWith('JP-')) return 'amazon.co.jp';
  if (code.startsWith('IN-')) return 'amazon.in';
  if (code.startsWith('MX-')) return 'amazon.com.mx';
  return 'amazon.com'; // Default to US
};

// Check if warehouse is an FBA warehouse
const isFBAWarehouse = (warehouseCode) => {
  return warehouseCode && warehouseCode.endsWith('-FBA');
};

function Warehouses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [view, setView] = useState('list');  // 'list' | 'brand-subsidiary'

  // Data
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseInventory, setWarehouseInventory] = useState([]);
  // Activity timeline (audit_log) for this warehouse — unconditional (rules of hooks),
  // mirroring the Suppliers detail so every entity has a consistent Activity section.
  const { events: warehouseTimelineEvents, loading: warehouseTimelineLoading } = useEntityTimeline('warehouse_management_route', selectedWarehouse?.id || null);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState({
    code: '',
    name: '',
    // Contact details (3PL / supplier contact for this warehouse)
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    // Delivery address (the warehouse's own address columns)
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    // Billing address (separate from delivery)
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: '',
    currency: '',
    warehouse_type: 'own',
    is_active: true
  });
  // Search box at the top of the list — case-insensitive substring match on
  // code / name / city / country / address.
  const [searchTerm, setSearchTerm] = useState('');
  // Active/Inactive filter — default to Active so deactivated warehouses are
  // hidden by default (Rishabh 2026-06-30: the list was showing inactive ones).
  const [statusFilter, setStatusFilter] = useState('active');
  // Delete confirmation state (soft-delete via deactivateWarehouse endpoint).
  const [deletingWarehouse, setDeletingWarehouse] = useState(null);
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  // References for the inline "Delete disabled because …" hint inside the
  // delete dialog. Populated lazily when the dialog opens; null means
  // still-loading, [] means safe-to-delete, [...] means blocked.
  const [deleteRefs, setDeleteRefs] = useState(null);
  const [deleteRefsLoading, setDeleteRefsLoading] = useState(false);

  // Probe references whenever the operator triggers the delete dialog.
  useEffect(() => {
    if (!deletingWarehouse) { setDeleteRefs(null); setDeleteRefsLoading(false); return; }
    let cancelled = false;
    setDeleteRefsLoading(true);
    api.get(`${API_BASE}/api/warehouses/${deletingWarehouse.id}/references`)
      .then(r => { if (!cancelled) setDeleteRefs(r?.data?.references || []); })
      .catch(() => { if (!cancelled) setDeleteRefs([]); }) // soft-fail: let backend re-reject on confirm
      .finally(() => { if (!cancelled) setDeleteRefsLoading(false); });
    return () => { cancelled = true; };
  }, [deletingWarehouse]);
  
  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Merge-duplicates dialog (re-point a duplicate warehouse into a keeper)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeFrom, setMergeFrom] = useState('');
  const [mergeTo, setMergeTo] = useState('');
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeBusy, setMergeBusy] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState('sku');
  const [sortDirection, setSortDirection] = useState('asc');

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />;
  };

  // Sorted inventory
  const sortedInventory = useMemo(() => {
    if (!warehouseInventory.length) return [];

    return [...warehouseInventory].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'sku':
          aVal = (a.sku || '').toLowerCase();
          bVal = (b.sku || '').toLowerCase();
          break;
        case 'asin':
          aVal = (a.asin || '').toLowerCase();
          bVal = (b.asin || '').toLowerCase();
          break;
        case 'current_quantity':
          aVal = parseFloat(a.current_quantity) || 0;
          bVal = parseFloat(b.current_quantity) || 0;
          break;
        case 'unit_cost':
          aVal = parseFloat(a.unit_cost) || 0;
          bVal = parseFloat(b.unit_cost) || 0;
          break;
        case 'value':
          aVal = (parseFloat(a.current_quantity) || 0) * (parseFloat(a.unit_cost) || 0);
          bVal = (parseFloat(b.current_quantity) || 0) * (parseFloat(b.unit_cost) || 0);
          break;
        case 'expiry_date':
          aVal = a.expiry_date || '9999-12-31';
          bVal = b.expiry_date || '9999-12-31';
          break;
        default:
          aVal = a[sortField] || '';
          bVal = b[sortField] || '';
      }

      let comparison = 0;
      if (typeof aVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = aVal - bVal;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [warehouseInventory, sortField, sortDirection]);

  // Get Amazon product image URL from ASIN
  const getAmazonImageUrl = (asin, warehouseCode) => {
    if (!asin) return null;
    // Amazon product images can be accessed via this pattern
    return `https://images-na.ssl-images-amazon.com/images/I/${asin}._SL75_.jpg`;
  };

  useEffect(() => {
    if (id) {
      loadWarehouseDetails(id);
    } else {
      loadWarehouses();
    }
  }, [id]);

  const loadWarehouses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await getWarehouses();
      setWarehouses(res?.warehouses || []);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
      setError('Failed to load warehouses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Only the newest warehouse load may apply its answer: an older one landing late (the user opened another warehouse, or went
  // back to the list) would show the wrong warehouse under this URL (DT-1139, same as Transfers DT-1113).
  const detailRequestRef = useRef(0);
  useEffect(() => { if (!id) detailRequestRef.current += 1; }, [id]);

  const loadWarehouseDetails = async (warehouseId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    // A different record: drop the previous one now, so a failed load never leaves it on screen under this URL.
    setSelectedWarehouse(prev => (prev && String(prev.id) === String(warehouseId) ? prev : null));
    setLoading(true);
    setError(null);

    try {
      const [warehouseRes, inventoryRes] = await Promise.all([
        getWarehouse(warehouseId),
        getWarehouseInventory(warehouseId)
      ]);
      if (stale()) return;

      // Backend returns { success, warehouse } and { success, warehouse, batches, summary }
      setSelectedWarehouse(warehouseRes?.warehouse || warehouseRes?.data);
      setWarehouseInventory(inventoryRes?.batches || inventoryRes?.data || []);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load warehouse details:', err);
      setError('Failed to load warehouse details. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  const handleOpenDialog = (warehouse = null) => {
    if (warehouse) {
      setEditingWarehouse(warehouse);
      setWarehouseForm({
        code: warehouse.code,
        name: warehouse.name,
        contact_first_name: warehouse.contact_first_name || '',
        contact_last_name: warehouse.contact_last_name || '',
        contact_email: warehouse.contact_email || '',
        address_line1: warehouse.address_line1 || warehouse.address || '',
        address_line2: warehouse.address_line2 || '',
        city: warehouse.city || '',
        state: warehouse.state || '',
        postal_code: warehouse.postal_code || '',
        country: warehouse.country || '',
        billing_address_line1: warehouse.billing_address_line1 || '',
        billing_address_line2: warehouse.billing_address_line2 || '',
        billing_city: warehouse.billing_city || '',
        billing_state: warehouse.billing_state || '',
        billing_postal_code: warehouse.billing_postal_code || '',
        billing_country: warehouse.billing_country || '',
        currency: warehouse.currency || '',
        warehouse_type: warehouse.warehouse_type || 'own',
        is_active: warehouse.is_active !== false
      });
    } else {
      setEditingWarehouse(null);
      setWarehouseForm({
        code: '',
        name: '',
        contact_first_name: '',
        contact_last_name: '',
        contact_email: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        billing_address_line1: '',
        billing_address_line2: '',
        billing_city: '',
        billing_state: '',
        billing_postal_code: '',
        billing_country: '',
        currency: '',
        warehouse_type: 'own',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  // Full-page (routed) Add/Edit warehouse form — replaces the old full-screen
  // Dialog overlay. openWarehousePage populates the form then puts ?form=<id|new>
  // on the LIST url (preserving any other params like from=contacts).
  // closeWarehousePage clears it — or returns to /contacts when the operator
  // arrived from there. Rishabh 2026-07-02.
  const openWarehousePage = (warehouseOrNew) => {
    if (warehouseOrNew === 'new') handleOpenDialog();
    else handleOpenDialog(warehouseOrNew);
    const p = new URLSearchParams(location.search);
    p.set('form', warehouseOrNew === 'new' ? 'new' : String(warehouseOrNew.id));
    navigate({ pathname: '/inventory/warehouses', search: `?${p.toString()}` });
  };
  const closeWarehousePage = () => {
    const wasEditing = !!editingWarehouse;
    setDialogOpen(false);
    setEditingWarehouse(null);
    if (fromContacts && wasEditing) { navigate('/contacts'); return; }
    const p = new URLSearchParams(location.search);
    p.delete('form');
    navigate({ pathname: '/inventory/warehouses', search: p.toString() ? `?${p.toString()}` : '' }, { replace: true });
  };

  // Deep-link from /contacts → "Add contact" → Warehouse opens this page with
  // ?new=1. Auto-open the existing Add Warehouse dialog then strip the param
  // so a refresh doesn't re-open it.
  useEffect(() => {
    if (!id && new URLSearchParams(location.search).get('new') === '1') {
      const p = new URLSearchParams(location.search);
      p.delete('new'); p.set('form', 'new');
      navigate({ pathname: '/inventory/warehouses', search: `?${p.toString()}` }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, id]);

  // Deep-link from /contacts "Import" → Warehouse opens this page with ?import=1.
  // Open the bulk-import dialog, then strip the param so a refresh doesn't re-open it.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (id || p.get('import') !== '1') return;
    setError(null); setImportDialogOpen(true);
    p.delete('import');
    navigate({ pathname: '/inventory/warehouses', search: p.toString() ? `?${p.toString()}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, id]);

  // Deep-link from /contacts Edit icon (?edit=1) bounces back to the list
  // view with edit_id=<warehouse-id> — the list-view useEffect below picks it
  // up and opens the edit dialog. We can't open the dialog directly from the
  // detail view because the Dialog component is only mounted in the list
  // return; React's conditional returns are mutually exclusive so calling
  // setDialogOpen(true) on detail view has no visible effect (Rishabh 2026-05-21
  // bug: clicking the edit pencil from Contacts dropped the operator on the
  // detail page with no dialog popping).
  useEffect(() => {
    if (!id || !selectedWarehouse) return;
    if (new URLSearchParams(location.search).get('edit') !== '1') return;
    const p = new URLSearchParams(location.search);
    p.delete('edit');
    p.set('form', String(selectedWarehouse.id));
    navigate(`/inventory/warehouses?${p.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse?.id]);

  // List-view side of the same deep-link: when ?edit_id=N is set on the list
  // page (after bounce-back above), find the warehouse in the loaded list and
  // open the edit dialog. Strips edit_id after firing.
  useEffect(() => {
    if (id) return;
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit_id');
    if (!editId) return;
    params.delete('edit_id');
    params.set('form', String(editId));
    navigate({ pathname: '/inventory/warehouses', search: `?${params.toString()}` }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ?from=contacts toggles Back button destination to /contacts.
  const fromContacts = new URLSearchParams(location.search).get('from') === 'contacts';

  // ?form=<id> (or ?form=new) renders the Add/Edit Warehouse form as a FULL PAGE
  // (its own URL + working Back), replacing the old full-screen Dialog overlay
  // (Rishabh 2026-07-02). Always lives on the LIST path so the detail route's
  // early-return doesn't pre-empt it.
  const formParam = new URLSearchParams(location.search).get('form');

  // Direct-load / refresh of ?form=<id>: populate the form from the loaded list
  // once available (a click already populated it via openWarehousePage; the
  // editingWarehouse guard makes this a no-op then and avoids clobbering edits).
  useEffect(() => {
    if (!formParam || formParam === 'new') return;
    if (editingWarehouse && String(editingWarehouse.id) === String(formParam)) return;
    const found = (warehouses || []).find(w => String(w.id) === String(formParam));
    if (found) handleOpenDialog(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formParam, warehouses]);

  const handleDeleteWarehouse = async () => {
    if (!deletingWarehouse) return;
    setDeletingInProgress(true);
    // If references probe came back empty, hard-delete via /permanent (matches
    // the supplier-delete pattern). Otherwise soft-delete via /deactivate.
    // Backend /permanent is strict — it re-checks refs server-side and 409s if
    // anything snuck in between probe + click, so the UI fall-through is safe.
    const safeToHardDelete = Array.isArray(deleteRefs) && deleteRefs.length === 0;
    try {
      if (safeToHardDelete) {
        await api.delete(`${API_BASE}/api/warehouses/${deletingWarehouse.id}/permanent`);
        setSuccess(`Warehouse "${deletingWarehouse.name}" permanently deleted`);
      } else {
        await api.post(`${API_BASE}/api/warehouses/${deletingWarehouse.id}/deactivate`);
        setSuccess(`Warehouse "${deletingWarehouse.name}" deactivated`);
      }
      setDeletingWarehouse(null);
      loadWarehouses();
    } catch (err) {
      const fallback = safeToHardDelete ? 'Failed to delete warehouse' : 'Failed to deactivate warehouse';
      setError(err.response?.data?.error || err.message || fallback);
    } finally {
      setDeletingInProgress(false);
    }
  };

  // Merge: dry-run preview (counts only, writes nothing) then apply.
  const previewMerge = async () => {
    if (!mergeFrom || !mergeTo || mergeFrom === mergeTo) { setError('Pick two different warehouses'); return; }
    setMergeBusy(true); setError(null); setMergePreview(null);
    try {
      const r = await api.post(`${API_BASE}/api/warehouses/merge`, { from_code: mergeFrom, to_code: mergeTo, dry_run: true });
      setMergePreview(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setMergeBusy(false); }
  };
  const doMerge = async () => {
    if (!mergePreview?.can_proceed) return;
    setMergeBusy(true); setError(null);
    try {
      const r = await api.post(`${API_BASE}/api/warehouses/merge`, { from_code: mergeFrom, to_code: mergeTo, dry_run: false });
      setSuccess(`Merged ${mergeFrom} → ${mergeTo}: ${r.data.rows_repointed || 0} reference(s) re-pointed; ${mergeFrom} deactivated.`);
      setMergeOpen(false); setMergePreview(null); setMergeFrom(''); setMergeTo('');
      loadWarehouses();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setMergeBusy(false); }
  };

  // Filter shown in the grid — case-insensitive substring match.
  const filteredWarehouses = warehouses.filter(w => {
    // Status filter (default Active). is_active is UInt8 (0/1) from the API, so
    // use truthiness rather than === false so both 0 and boolean false match.
    if (statusFilter === 'active' && !w.is_active) return false;
    if (statusFilter === 'inactive' && w.is_active) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return ['code', 'name', 'city', 'country', 'address_line1', 'address'].some(
      f => String(w[f] || '').toLowerCase().includes(q)
    );
  });

  const handleSave = async () => {
    try {
      setError(null);
      if (editingWarehouse) {
        await updateWarehouse(editingWarehouse.id, warehouseForm);
        setSuccess('Warehouse updated successfully');
      } else {
        await createWarehouse(warehouseForm);
        setSuccess('Warehouse created successfully');
      }
      setDialogOpen(false);
      loadWarehouses();
      // Return to /contacts if the operator arrived from there, else strip the
      // ?form param to drop back onto the warehouse list (full-page form).
      if (fromContacts && editingWarehouse) {
        navigate('/contacts');
      } else {
        const p = new URLSearchParams(location.search);
        p.delete('form');
        navigate({ pathname: '/inventory/warehouses', search: p.toString() ? `?${p.toString()}` : '' }, { replace: true });
      }
      setEditingWarehouse(null);
    } catch (err) {
      setError(err.message || 'Failed to save warehouse');
    }
  };

  const calculateWarehouseStats = (warehouse) => {
    // This would typically come from the backend
    return {
      skuCount: 0,
      totalUnits: 0,
      totalValue: 0
    };
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setImportFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }
    
    try {
      setImporting(true);
      setError(null);
      
      const result = await bulkImportWarehouses(importFile);
      setSuccess(`Import completed: ${result.success_count || 0} succeeded, ${result.error_count || 0} failed`);
      
      setImportDialogOpen(false);
      setImportFile(null);
      loadWarehouses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import warehouses');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Warehouse Detail View
  if (id && selectedWarehouse) {
    const totalUnits = warehouseInventory.reduce((sum, b) => sum + (parseFloat(b.current_quantity) || 0), 0);
    const totalValue = warehouseInventory.reduce((sum, b) => {
      const qty = parseFloat(b.current_quantity) || 0;
      const cost = parseFloat(b.unit_cost) || 0;
      return sum + (qty * cost);
    }, 0);

    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(fromContacts ? '/contacts' : '/inventory/warehouses')}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              {selectedWarehouse.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedWarehouse.code} • {[selectedWarehouse.city, formatCountry(selectedWarehouse.country)].filter(Boolean).join(', ')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isFBAWarehouse(selectedWarehouse.code) && (
              <Chip
                label="Amazon FBA"
                color="warning"
                size="small"
              />
            )}
            <Chip
              label={selectedWarehouse.is_active ? 'Active' : 'Inactive'}
              color={selectedWarehouse.is_active ? 'success' : 'default'}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Unique SKUs</Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatNumber(new Set(warehouseInventory.map(b => b.sku)).size)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Total Units</Typography>
                <Typography variant="h4" fontWeight="bold">{formatNumber(totalUnits)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Inventory Value</Typography>
                <Typography variant="h4" fontWeight="bold">{formatCurrency(totalValue)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Activity — audit_log entries for this warehouse (consistent with Suppliers) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Activity</Typography>
            <Timeline
              events={warehouseTimelineEvents.map(e => ({
                ...e,
                timestamp: e.timestamp ? String(e.timestamp).replace('T', ' ').slice(0, 16) : '',
              }))}
              loading={warehouseTimelineLoading}
              empty={<Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No activity yet.</Typography>}
            />
          </CardContent>
        </Card>

        {/* Inventory Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Inventory</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {isFBAWarehouse(selectedWarehouse?.code) && (
                      <TableCell width={60}>Image</TableCell>
                    )}
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('sku')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        SKU
                        {renderSortIcon('sku')}
                      </Box>
                    </TableCell>
                    {isFBAWarehouse(selectedWarehouse?.code) && (
                      <TableCell
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('asin')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          ASIN
                          {renderSortIcon('asin')}
                        </Box>
                      </TableCell>
                    )}
                    {!isFBAWarehouse(selectedWarehouse?.code) && (
                      <TableCell>Batch</TableCell>
                    )}
                    <TableCell
                      align="right"
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('current_quantity')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Quantity
                        {renderSortIcon('current_quantity')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('unit_cost')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Unit Cost
                        {renderSortIcon('unit_cost')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('value')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Value
                        {renderSortIcon('value')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('expiry_date')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Expiry
                        {renderSortIcon('expiry_date')}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedInventory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isFBAWarehouse(selectedWarehouse?.code) ? 8 : 6} align="center">
                        <Box sx={{ py: 4 }}>
                          <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography color="textSecondary">
                            No inventory in this warehouse
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedInventory.map((batch, idx) => {
                      const amazonDomain = getAmazonDomain(selectedWarehouse?.code);
                      const isFBA = isFBAWarehouse(selectedWarehouse?.code);

                      return (
                        <TableRow key={batch.id || idx} hover>
                          {isFBA && (
                            <TableCell>
                              {batch.asin ? (
                                <Box
                                  component="img"
                                  src={`https://ws-na.amazon-adsystem.com/widgets/q?_encoding=UTF8&ASIN=${batch.asin}&Format=_SL75_&ID=AsinImage&MarketPlace=${amazonDomain.includes('co.uk') ? 'GB' : amazonDomain.includes('com.au') ? 'AU' : amazonDomain.includes('.ca') ? 'CA' : amazonDomain.includes('.de') ? 'DE' : 'US'}&ServiceVersion=20070822&WS=1`}
                                  alt={batch.sku}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: 'contain',
                                    borderRadius: 0.5,
                                    bgcolor: 'grey.100'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<span style="color: #999; font-size: 20px;">📦</span>';
                                  }}
                                />
                              ) : (
                                <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 0.5 }}>
                                  <ImageIcon sx={{ color: 'grey.400' }} />
                                </Box>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography fontWeight="medium">{batch.sku}</Typography>
                          </TableCell>
                          {isFBA ? (
                            <TableCell>
                              {batch.asin ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" fontFamily="monospace">
                                    {batch.asin}
                                  </Typography>
                                  <Tooltip title={`View on ${amazonDomain}`}>
                                    <IconButton
                                      size="small"
                                      href={`https://${amazonDomain}/dp/${batch.asin}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ ml: 0.5 }}
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : '—'}
                            </TableCell>
                          ) : (
                            <TableCell>{batch.batch_number || '—'}</TableCell>
                          )}
                          <TableCell align="right">{formatNumber(batch.current_quantity)}</TableCell>
                          <TableCell align="right">
                            {batch.unit_cost ? formatCurrency(batch.unit_cost) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {batch.unit_cost
                              ? formatCurrency((parseFloat(batch.current_quantity) || 0) * (parseFloat(batch.unit_cost) || 0))
                              : '—'
                            }
                          </TableCell>
                          <TableCell>
                            {batch.expiry_date && batch.expiry_date !== '0000-00-00' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {batch.expiry_date}
                                {batch.days_until_expiry && batch.days_until_expiry <= 30 && (
                                  <Chip
                                    label={`${batch.days_until_expiry}d`}
                                    size="small"
                                    color={batch.days_until_expiry <= 7 ? 'error' : 'warning'}
                                  />
                                )}
                              </Box>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Warehouse List View
  if (view === 'brand-subsidiary') {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">Brand → Subsidiary Overrides</Typography>
          <Button variant="outlined" startIcon={<BackIcon />} onClick={() => setView('list')}>
            Back to Warehouses
          </Button>
        </Box>
        <WarehouseBrandSubsidiaryMatrix />
      </Box>
    );
  }

  return (
    <Box>
      {/* List view — hidden while the full-page warehouse form is open (?form=…) */}
      {!formParam && (<>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Warehouses
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage warehouse locations and view inventory by location
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={() => setView('brand-subsidiary')}
          >
            Brand → Subsidiary
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => { setError(null); setImportDialogOpen(true); }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openWarehousePage('new')}
          >
            Add Warehouse
          </Button>
          <Button
            variant="outlined"
            onClick={() => { setMergePreview(null); setMergeFrom(''); setMergeTo(''); setError(null); setMergeOpen(true); }}
          >
            Merge Duplicates
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadWarehouses}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Merge duplicates dialog — dry-run preview then apply */}
      <Dialog open={mergeOpen} onClose={() => !mergeBusy && setMergeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge duplicate warehouse</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Re-points every inventory / transfer / PO reference from the duplicate onto the keeper, moves the supplier link, then deactivates the duplicate. Preview first — nothing is written until you confirm.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Merge (duplicate)</InputLabel>
              <Select label="Merge (duplicate)" value={mergeFrom} onChange={(e) => { setMergeFrom(e.target.value); setMergePreview(null); }}>
                {warehouses.map(w => <MenuItem key={w.id} value={w.code}>{w.code} — {w.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Into (keeper)</InputLabel>
              <Select label="Into (keeper)" value={mergeTo} onChange={(e) => { setMergeTo(e.target.value); setMergePreview(null); }}>
                {warehouses.filter(w => w.code !== mergeFrom).map(w => <MenuItem key={w.id} value={w.code}>{w.code} — {w.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {mergePreview && (
            <Alert severity={mergePreview.can_proceed ? 'info' : 'warning'} sx={{ mt: 1 }}>
              {mergePreview.can_proceed ? (
                <>
                  <strong>Safe to merge.</strong> References to re-point:{' '}
                  {(mergePreview.updatable || []).filter(u => u.count > 0).map(u => `${u.table}.${u.col} (${u.count})`).join(', ') || 'none — only deactivates the empty duplicate'}.
                  {mergePreview.supplier_move ? ' Supplier link moves to the keeper.' : ''}
                </>
              ) : (
                <>
                  <strong>Can't auto-merge.</strong> These hold rows that can't be updated in place — handle manually first:{' '}
                  {(mergePreview.blocked_with_rows || []).map(b => `${b.table}.${b.col} (${b.count}, ${b.reason})`).join('; ')}.
                </>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)} disabled={mergeBusy}>Cancel</Button>
          <Button onClick={previewMerge} disabled={mergeBusy || !mergeFrom || !mergeTo || mergeFrom === mergeTo}>
            {mergeBusy && !mergePreview ? 'Checking…' : 'Preview'}
          </Button>
          <Button variant="contained" color="warning" onClick={doMerge} disabled={mergeBusy || !mergePreview?.can_proceed}>
            {mergeBusy ? 'Merging…' : 'Merge + Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters — status (default Active) + case-insensitive search across code/name/city/country/address. */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search warehouses (code, name, city, country, address)…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {searchTerm && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {filteredWarehouses.length} of {warehouses.length} warehouses match
        </Typography>
      )}

      {/* Warehouse Grid */}
      <Grid container spacing={3}>
        {filteredWarehouses.length === 0 ? (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <WarehouseIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="textSecondary">
                  No warehouses configured
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => openWarehousePage('new')}
                  sx={{ mt: 2 }}
                >
                  Add First Warehouse
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredWarehouses.map((warehouse) => (
            <Grid item xs={12} sm={6} md={4} key={warehouse.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => navigate(`/inventory/warehouses/${warehouse.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarehouseIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        {warehouse.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openWarehousePage(warehouse);
                        }}
                        title="Edit warehouse"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {warehouse.is_active && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setError(null); setDeletingWarehouse(warehouse);
                          }}
                          title="Delete or deactivate warehouse"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  
                  <Chip 
                    label={warehouse.code} 
                    size="small" 
                    variant="outlined" 
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {warehouse.address || 'No address'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {[warehouse.city, formatCountry(warehouse.country)].filter(Boolean).join(', ') || 'No location'}
                  </Typography>
                  
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Chip 
                      label={warehouse.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={warehouse.is_active ? 'success' : 'default'}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      </>)}

      {/* Add/Edit Warehouse — FULL PAGE (routed via ?form=…), replaces the old
          full-screen Dialog overlay so it has its own URL + working Back.
          Rishabh 2026-07-02. Fields below are unchanged. */}
      {formParam && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={closeWarehousePage} aria-label="Back to warehouses"><BackIcon /></IconButton>
            <Typography variant="h5" fontWeight={600}>{editingWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}</Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Code"
                  value={warehouseForm.code}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, code: e.target.value.toUpperCase() })}
                  required
                  fullWidth
                  placeholder="e.g., WH01"
                  inputProps={{ maxLength: 10 }}
                  // Code is the warehouse business key (stock, orders & transfers
                  // reference it) — immutable after creation. Editable only when
                  // adding a new warehouse.
                  disabled={!!editingWarehouse}
                  helperText={editingWarehouse ? "Code can't be changed — it's referenced by stock, orders & transfers. Create a new warehouse to use a different code." : ''}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  label="Name"
                  value={warehouseForm.name}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, name: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 11, letterSpacing: 0.5 }}>Contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="First name" fullWidth value={warehouseForm.contact_first_name}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, contact_first_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Last name" fullWidth value={warehouseForm.contact_last_name}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, contact_last_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Email" type="email" fullWidth value={warehouseForm.contact_email}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, contact_email: e.target.value })} />
              </Grid>
            </Grid>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 11, letterSpacing: 0.5 }}>Delivery Address</Typography>
            <TextField
              label="Address line 1"
              value={warehouseForm.address_line1}
              onChange={(e) => setWarehouseForm({ ...warehouseForm, address_line1: e.target.value })}
              fullWidth
            />
            <TextField
              label="Address line 2"
              value={warehouseForm.address_line2}
              onChange={(e) => setWarehouseForm({ ...warehouseForm, address_line2: e.target.value })}
              fullWidth
              placeholder="Suite, unit, building (optional)"
            />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="City"
                  value={warehouseForm.city}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, city: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="State / Region"
                  value={warehouseForm.state}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, state: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Postal code"
                  value={warehouseForm.postal_code}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, postal_code: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  label="Country"
                  value={warehouseForm.country}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, country: e.target.value })}
                  fullWidth
                  placeholder="e.g. UK, AU, US"
                />
              </Grid>
              <Grid item xs={4} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="wh-currency-lbl">Currency</InputLabel>
                  <Select labelId="wh-currency-lbl" label="Currency"
                    value={warehouseForm.currency || ''}
                    onChange={(e) => setWarehouseForm({ ...warehouseForm, currency: e.target.value })}>
                    {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 11, letterSpacing: 0.5 }}>Billing Address</Typography>
              <Button size="small" sx={{ fontSize: 11, py: 0.25 }} onClick={() => setWarehouseForm(f => ({
                ...f,
                billing_address_line1: f.address_line1, billing_address_line2: f.address_line2,
                billing_city: f.city, billing_state: f.state, billing_postal_code: f.postal_code, billing_country: f.country,
              }))}>Same as delivery</Button>
            </Box>
            <TextField label="Billing address line 1" fullWidth value={warehouseForm.billing_address_line1}
              onChange={(e) => setWarehouseForm({ ...warehouseForm, billing_address_line1: e.target.value })} />
            <TextField label="Billing address line 2" fullWidth value={warehouseForm.billing_address_line2}
              onChange={(e) => setWarehouseForm({ ...warehouseForm, billing_address_line2: e.target.value })} placeholder="Suite, unit, building (optional)" />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}><TextField label="Billing city" fullWidth value={warehouseForm.billing_city}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, billing_city: e.target.value })} /></Grid>
              <Grid item xs={6} sm={4}><TextField label="Billing state / region" fullWidth value={warehouseForm.billing_state}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, billing_state: e.target.value })} /></Grid>
              <Grid item xs={6} sm={4}><TextField label="Billing postal code" fullWidth value={warehouseForm.billing_postal_code}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, billing_postal_code: e.target.value })} /></Grid>
              <Grid item xs={6} sm={4}><TextField label="Billing country" fullWidth value={warehouseForm.billing_country}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, billing_country: e.target.value })} placeholder="e.g. UK, AU, US" /></Grid>
            </Grid>
            <FormControl size="small" fullWidth>
              <InputLabel id="warehouse-type-lbl">Warehouse Type</InputLabel>
              <Select
                labelId="warehouse-type-lbl"
                label="Warehouse Type"
                value={warehouseForm.warehouse_type || 'own'}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, warehouse_type: e.target.value })}
              >
                <MenuItem value="own">Own — your physical warehouse</MenuItem>
                <MenuItem value="supplier">Supplier — RM supplier's virtual warehouse (ship-to bucket)</MenuItem>
                <MenuItem value="manufacturer">Manufacturer — contract manufacturer's facility (assembly + FG receipt)</MenuItem>
                <MenuItem value="git">GIT — Goods In Transit (accounting bucket; never use as ship-to)</MenuItem>
                <MenuItem value="legacy">Legacy — deprecated, hidden from pickers</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={warehouseForm.is_active}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3, maxWidth: 1100, mx: 'auto', width: '100%' }}>
            <Button onClick={closeWarehousePage}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!warehouseForm.code || !warehouseForm.name}
            >
              {editingWarehouse ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Warehouses from CSV</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload a CSV file to bulk import warehouse locations. Download the template below to see the required format.
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadCSVTemplate('warehouses')}
              sx={{ mb: 3 }}
              fullWidth
            >
              Download CSV Template
            </Button>

            <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Required columns: code, name
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Optional: address, city, country, is_primary
              </Typography>
            </Box>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="warehouse-file-upload"
            />
            <label htmlFor="warehouse-file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 2 }}
              >
                Select CSV File
              </Button>
            </label>

            {importFile && (
              <Alert severity="info">
                Selected file: {importFile.name}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setImportFile(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — soft-delete via /:id/deactivate. Inline shows
          the reference list (POs / GRs / transfers / inventory_batches /
          suppliers) when the warehouse can't be deleted, matching the
          "Delete disabled. This Branch has an order history." pattern. */}
      <Dialog open={Boolean(deletingWarehouse)} onClose={() => !deletingInProgress && setDeletingWarehouse(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {Array.isArray(deleteRefs) && deleteRefs.length === 0
            ? 'Delete warehouse permanently'
            : 'Deactivate warehouse'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2">
            {Array.isArray(deleteRefs) && deleteRefs.length === 0
              ? <>Permanently delete <strong>{deletingWarehouse?.name}</strong> ({deletingWarehouse?.code})? This cannot be undone.</>
              : <>Deactivate <strong>{deletingWarehouse?.name}</strong> ({deletingWarehouse?.code})?</>}
          </Typography>
          {deleteRefsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Checking references…</Typography>
            </Box>
          )}
          {!deleteRefsLoading && Array.isArray(deleteRefs) && deleteRefs.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }} icon={false}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Delete disabled — this warehouse has activity
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0, fontSize: 12 }}>
                {deleteRefs.map((r) => (
                  <li key={r.entity}>
                    <Typography variant="caption" component="div">
                      <strong>{r.entity}:</strong> {Number(r.n).toLocaleString()}
                      {r.entity === 'inventory_batches' && Number(r.qty) > 0 && (
                        <> ({Number(r.qty).toLocaleString()} units)</>
                      )}
                      {Array.isArray(r.samples) && r.samples.length > 0 && (
                        <Box component="span" sx={{ display: 'block', fontSize: 11, opacity: 0.85, mt: 0.25, ml: 0 }}>
                          {r.samples.map((s, i) => (
                            <React.Fragment key={`${r.entity}-${s.id || i}`}>
                              {i > 0 && ', '}
                              <Box
                                component="a"
                                href={s.route}
                                onClick={(e) => { e.preventDefault(); navigate(s.route); }}
                                sx={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                              >
                                {s.label || s.id}
                              </Box>
                            </React.Fragment>
                          ))}
                          {Number(r.n) > r.samples.length && <> …and {Number(r.n) - r.samples.length} more</>}
                        </Box>
                      )}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Clear these references first (cancel/delete the POs, drain the stock, etc.) before deactivating.
              </Typography>
            </Alert>
          )}
          {!deleteRefsLoading && Array.isArray(deleteRefs) && deleteRefs.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              No referenced inventory, POs, or transfers — safe to permanently delete.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingWarehouse(null)} disabled={deletingInProgress}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteWarehouse}
            disabled={deletingInProgress || deleteRefsLoading || (Array.isArray(deleteRefs) && deleteRefs.length > 0)}
          >
            {deletingInProgress
              ? <CircularProgress size={18} />
              : (Array.isArray(deleteRefs) && deleteRefs.length === 0 ? 'Delete permanently' : 'Deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Warehouses;
